import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import { ErrorDiv } from '../../components/utilities/commonUtilities/FormElements';
import { ReactComponent as Logo } from '../../resources/images/logo.svg';
import { CONTENT_MIN_WIDTH, styleBreakMobile, styleBreakSmallMobile, styleBreakDesktop } from '../../components/utilities/layouts/Globals';
import { getCustomColors } from '../../styles/CustomColors';
import { FONT_WEIGHT_SEMI } from '../utilities/Material';
import { PrimaryButton } from '../../components/utilities/commonUtilities/GenericButtonElements';
import { ContentLayout } from '../../components/utilities/layouts/ContentLayout';
import { Typography } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import { CodeInput } from '../../components/utilities/commonUtilities/FormElements';
import AlertModalYesNo from '../../components/utilities/AlertModal/AlertModalYesNo';
import { authenticateDevice, resendDeviceAuthenticationAnswer } from '../../apis/authentication';
import { PATH_HOME } from '../../containers/RoutePaths';
import { Redirect } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import { connect } from 'react-redux';
import { setDeviceToken } from '../../apis/api';
import { persistDeviceToken } from '../../utils/ClientSessionHandler';
import { CURRENT_APP_THEME } from '../../styles/stylesSelector';
import { ReactComponent as ShieldSvg } from '../../resources/images/shield.svg';
import { ReactComponent as ShadowSvg } from '../../resources/images/shadow.svg';
import { FONT_WEIGHT_BOLD } from '../utilities/Material';
import { ReactComponent as SquareFilledIcon } from '../../resources/images/square-filled.svg';
import { ReactComponent as SquareIcon } from '../../resources/images/square.svg';

const Colors = getCustomColors();

const useStyles = makeStyles(theme => ({
    logo: {
        display: 'block',
        height: '28px',
        width: '132px',
        [styleBreakMobile(theme)]: {
            height: '24px',
            width: '113px',
        },
    },
    shield: {
        display: 'block',
        height: '364px',
        width: '364px',
        [styleBreakMobile(theme)]: {
            height: '80px',
            width: '80px',
        },
    },
    shadow: {
        display: 'block',
        height: '30px',
        width: '364px',
        [styleBreakMobile(theme)]: {
            height: '10px',
            width: '80px',
        },
    },
    codeInput: {
        width: '327px',
        [styleBreakMobile(theme)]: {
            width: '100%',
        },
        '& ::placeholder': {
            color: Colors.COLOR_GREY.main,
            letterSpacing: '0px',
            fontSize: '16px',
            fontWeight: FONT_WEIGHT_SEMI,
            opacity: 1,
        },
    },
    header: {
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        background: Colors.COLOR_WHITE,
        boxShadow: `0px 1px 4px rgba(186, 186, 186, 0.25)`,
        zIndex: '9999',
        justifyContent: 'flex-start',
        marginBottom: '1px',
        [styleBreakDesktop(theme)]: {
            paddingLeft: theme.spacing(6),
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
        },
    },
    lowerContent: {
        width: '327px',
        display: 'flex',
        marginTop: theme.spacing(6),
        background: Colors.COLOR_WHITE,
        flexDirection: 'column',
        [styleBreakMobile(theme)]: {
            left: theme.spacing(3),
            right: theme.spacing(3),
            width: 'calc(100% - 48px)',
            minWidth: CONTENT_MIN_WIDTH,
            position: 'fixed',
            bottom: theme.spacing(3),
            marginTop: theme.spacing(0),
        },
        [styleBreakSmallMobile(theme)]: {
            left: theme.spacing(2),
            right: theme.spacing(2),
            width: 'calc(100% - 32px)',
        },
    },
    button: {
        width: '100%',
        minWidth: theme.spacing(35),
        marginRight: theme.spacing(4),
        [styleBreakMobile(theme)]: {
            marginTop: theme.spacing(2),
        },
        [styleBreakDesktop(theme)]: {
            order: '1',
        },
    },
    resendElement: {
        cursor: 'pointer',
        fontSize: theme.spacing(2),
        textAlign: 'center',
        letterSpacing: '0.16px',
        color: theme.palette.primary.dark,
        [styleBreakDesktop(theme)]: {
            order: '2',
            marginTop: '12px',
            marginBottom: '8px',
        },
    },
    contentTitle: {
        paddingTop: theme.spacing(2.5),
        marginBottom: theme.spacing(1),
        // Special font weight.
        fontWeight: '700',
        [styleBreakDesktop(theme)]: {
            fontSize: '40px',
        },
    },
    inputTitle: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(1),
        [styleBreakMobile(theme)]: {
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(0.5),
        },
    },
    remember: {
        marginTop: theme.spacing(2),
    },
    emailAddress: {
        fontWeight: FONT_WEIGHT_BOLD,
    },
    // We need this to override the theme root background to get the white background.
    fullscreenLayoutDiv: {
        background: Colors.COLOR_WHITE,
        height: '100vh',
        [styleBreakMobile(theme)]: {
            height: '95vh',
            paddingTop: theme.spacing(3),
        },
        [styleBreakDesktop(theme)]: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            minWidth: '100%',
            marginLeft: '0px',
            marginRight: '0px',
            paddingTop: theme.spacing(13),
            paddingLeft: '130px',
            paddingRight: '200px',
        },
        width: '100%',
    },
}));

const onePlusAuthenticationResult = {
    SUCCESS: {
        type: 'SUCCESS',
        msg: '',
    },
    ERROR_INCORRECT_ANSWER: {
        type: 'ERROR_INCORRECT_ANSWER',
        msg: 'Incorrect code, please try again.',
    },
    ERROR_ANSWER_EXPIRED: {
        type: 'ERROR_ANSWER_EXPIRED',
        msg: 'This code has expired. Select resend code to receive a new verification code.',
    },
    // NO_ANSWER_FOUND error will display answer expiry as well.
    ERROR_NO_ANSWER_FOUND: {
        type: 'ERROR_NO_ANSWER_FOUND',
        msg: 'This code has expired. Select resend code to receive a new verification code.',
    },
    ERROR_NO_MORE_ATTEMPTS: {
        type: 'ERROR_NO_MORE_ATTEMPTS',
        msg: 'You have reached the maximum number of incorrect attempts. Select resend code to receive a new verification code.',
    },
    ERROR_CATCH_ALL: {
        type: 'ERROR_CATCH_ALL',
        msg: 'Something went wrong with verification.',
    },
};

function Header() {
    const classes = useStyles();
    return (
        <Box display="flex" width="100%" className={classes.header}>
            <Logo aria-labelledby={`${CURRENT_APP_THEME} logo`} className={classes.logo} />
        </Box>
    );
}

function LowerContent({ buttonOnClick, resendOnClick, buttonText, buttonDisabled, submitting }) {
    const classes = useStyles();
    return (
        <Box className={classes.lowerContent}>
            <Typography variant="h3" onClick={resendOnClick} className={classes.resendElement}>
                Resend Code
            </Typography>
            <PrimaryButton onClick={buttonOnClick} className={classes.button} disabled={buttonDisabled}>
                {submitting ? <CircularProgress size={20} /> : buttonText}
            </PrimaryButton>
        </Box>
    );
}

function ContentHolder({ children, buttonText, resendOnClick, buttonOnClick, buttonDisabled, submitting }) {
    const classes = useStyles();
    return (
        <ContentLayout className={classes.fullscreenLayoutDiv}>
            <Box display="flex" flexDirection="column" alignItems="left">
                <ShieldSvg className={classes.shield} />
                <ShadowSvg className={classes.shadow} />
            </Box>
            <Box display="flex" flexDirection="column">
                {children}
                <LowerContent
                    buttonOnClick={buttonOnClick}
                    resendOnClick={resendOnClick}
                    buttonText={buttonText}
                    buttonDisabled={buttonDisabled}
                    submitting={submitting}
                />
            </Box>
        </ContentLayout>
    );
}

const hasError = resultType => {
    return (
        resultType === onePlusAuthenticationResult.ERROR_ANSWER_EXPIRED.type ||
        resultType === onePlusAuthenticationResult.ERROR_INCORRECT_ANSWER.type ||
        resultType === onePlusAuthenticationResult.ERROR_NO_ANSWER_FOUND.type ||
        resultType === onePlusAuthenticationResult.ERROR_NO_MORE_ATTEMPTS.type ||
        resultType === onePlusAuthenticationResult.ERROR_CATCH_ALL.type
    );
};

function Content({ checked, onCheckBoxChange, onCodeChange, code, resultType, setResultType, emailAddress }) {
    const classes = useStyles();
    return (
        <>
            <Typography variant="h1" component="div" className={classes.contentTitle}>
                Verification
            </Typography>
            <Typography variant="body1" component="div">
                To help protect your account, we've sent a 4-digit verification code to{' '}
                <Typography variant="body2" component="span" className={classes.emailAddress}>
                    {emailAddress}
                </Typography>
                .
            </Typography>
            <Typography variant="body1" component="div">
                Enter the code below to continue.
            </Typography>
            <Typography variant="body2" component="div" className={classes.inputTitle}>
                Verification Code
            </Typography>
            <CodeInput
                name="code"
                value={code}
                onChange={onCodeChange}
                error={hasError(resultType)}
                className={classes.codeInput}
                setResultType={setResultType}
            />
            {hasError(resultType) && (
                <div>
                    <ErrorDiv text={onePlusAuthenticationResult[resultType]?.msg} />
                </div>
            )}
            <Box display="flex" alignItems="center" className={classes.remember}>
                <Checkbox
                    checked={checked}
                    onChange={onCheckBoxChange}
                    icon={<SquareIcon />}
                    checkedIcon={<SquareFilledIcon />}
                    inputProps={{ 'aria-label': 'checkbox' }}
                    color="secondary"
                    style={{
                        color: 'BLACK',
                        // Need to move the underlying svg to line up.
                        paddingLeft: '0px',
                        marginLeft: '-3px',
                    }}
                />
                <Typography variant="body1" component="div">
                    Remember this device for 30 days
                </Typography>
            </Box>
        </>
    );
}

function OnePlusAuthentication({ emailAddress }) {
    const [requestCookie, setRequestCookie] = useState(false);
    const [code, setCode] = useState('');
    const [resultType, setResultType] = useState('');
    const [displayModal, setDisplayModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    if (resultType === onePlusAuthenticationResult.SUCCESS.type) {
        return <Redirect to={PATH_HOME} />;
    }

    const onCodeChange = e => {
        setCode(e.target.value.trim());
    };
    const onCheckBoxChange = event => {
        setRequestCookie(event.target.checked);
    };
    const buttonOnClick = () => {
        setSubmitting(true);
        performDeviceAuthentication(code, setResultType, setSubmitting, requestCookie);
    };
    const resendOnClick = () => {
        setResultType(''); // clear result
        resendCode();
        setDisplayModal(true);
    };
    const onModalAcknowledge = () => {
        setDisplayModal(false);
    };

    return (
        <>
            <Header />
            <ContentHolder
                buttonOnClick={buttonOnClick}
                resendOnClick={resendOnClick}
                buttonText={'Submit'}
                buttonDisabled={code === ''}
                submitting={submitting}
            >
                <Content
                    checked={requestCookie}
                    onCheckBoxChange={onCheckBoxChange}
                    onCodeChange={onCodeChange}
                    code={code}
                    resultType={resultType}
                    setResultType={setResultType}
                    emailAddress={emailAddress}
                />
            </ContentHolder>
            <AlertModalYesNo
                isDisplayed={displayModal}
                alertModalTitle={'Verification Code Sent'}
                alertModalBody={'Check your email for your new 4-digit verification code.'}
                onYesSelected={onModalAcknowledge}
                yesButtonText={'Ok'}
                onCloseSelected={onModalAcknowledge}
            />
        </>
    );
}

/** connect to redux store **/
const mapStateToProps = ({ login }) => {
    return {
        emailAddress: login?.session?.extraFields?.emailAddress,
    };
};

export default connect(mapStateToProps, null)(OnePlusAuthentication);

/** API layer **/
async function performDeviceAuthentication(answerCode, setResultType, setSubmitting, requestCookie) {
    try {
        const response = await authenticateDevice(answerCode, requestCookie);
        setSubmitting(false);
        setResultType(response.data.result);
        if (response.data.deviceToken) {
            setDeviceToken(response.data.deviceToken);
            persistDeviceToken(response.data.deviceToken);
        }
    } catch (error) {
        setSubmitting(false);
        setResultType('ERROR_CATCH_ALL');
        console.log(error);
    }
}

async function resendCode() {
    try {
        await resendDeviceAuthenticationAnswer();
    } catch (error) {
        console.log(error);
    }
}
